import {useCallback, useEffect, useRef, useState} from "react";

import type {ISuggestionsQueryResult} from "../types/ISuggestionsQueryResult";
import type {IUseSuggestionsQueryOptions} from "../types/IUseSuggestionsQueryOptions";
import type {SearchComboboxStatus} from "../types/SearchComboboxStatus";
import {useLatestRef} from "./use_latest_ref";

const DEFAULT_DEBOUNCE_MS = 200;
const DEFAULT_MIN_LENGTH = 2;
const DEFAULT_SLOW_AFTER_MS = 300;

const defaultGetCacheKey = (query: string): string => query.trim().toLowerCase().replace(/\s+/g, " ");
const isNeverEmpty = (): boolean => false;

/** `retry` and `abort` apply to the phrase they were called for; typing something else drops them. */
interface IQueryCommand {
    key: string;
    type: "retry" | "abort";
    nonce: number;
}

interface IQueryState<T> {
    /** Run (phrase + command) this state describes; a mismatch with the current run means the effect has not caught up yet. */
    runId: string | null;
    status: SearchComboboxStatus;
    data: T | null;
    isSlow: boolean;
    /** Last successful result, the source of `staleData`. Dropped whenever the hook goes idle. */
    lastSuccess: T | null;
}

const IDLE_STATE: IQueryState<never> = {runId: null, status: "idle", data: null, isSlow: false, lastSuccess: null};

const toResolvedStatus = <T>(data: T, isEmpty: (data: T) => boolean): SearchComboboxStatus => (isEmpty(data) ? "empty" : "success");

/**
 * Lifecycle of a suggestions request for a typed phrase: debounce, one `AbortController` per request, cache, retry
 * and the "slow" flag. Aborts are detected with `signal.aborted` (request libraries such as `@pg-mono/request` wrap
 * the `AbortError`) and never surface as `error`; a request id guards against late responses on top of that.
 * Until `slowAfterMs` passes the previous result stays available as `staleData`, then `isSlow` takes over.
 */
export const useSuggestionsQuery = <T>(options: IUseSuggestionsQueryOptions<T>): ISuggestionsQueryResult<T> => {
    const {query, minLength = DEFAULT_MIN_LENGTH, isEnabled = true, getCacheKey = defaultGetCacheKey} = options;
    const trimmedQuery = query.trim();
    const key = isEnabled && trimmedQuery.length >= minLength ? getCacheKey(trimmedQuery) : null;

    const [command, setCommand] = useState<IQueryCommand | null>(null);
    const [state, setState] = useState<IQueryState<T>>(IDLE_STATE);
    const requestIdRef = useRef(0);
    const nonceRef = useRef(0);
    const cancelRef = useRef<(() => void) | null>(null);
    const latestRef = useLatestRef({
        key,
        trimmedQuery,
        fetcher: options.fetcher,
        cache: options.cache,
        isEmpty: options.isEmpty ?? isNeverEmpty,
        debounceMs: options.debounceMs ?? DEFAULT_DEBOUNCE_MS,
        slowAfterMs: options.slowAfterMs ?? DEFAULT_SLOW_AFTER_MS
    });

    const activeCommand = command && command.key === key ? command : null;
    const isAborted = activeCommand?.type === "abort";
    const isRetry = activeCommand?.type === "retry";
    const runId = key === null || isAborted ? null : `${key}\u0000${activeCommand ? activeCommand.nonce : ""}`;

    useEffect(() => {
        if (command && !activeCommand) {
            setCommand(null);
        }
    }, [command, activeCommand]);

    useEffect(() => {
        if (runId === null) {
            setState((previous) => (previous.status === "idle" && previous.lastSuccess === null ? previous : IDLE_STATE));
            return;
        }

        const {cache, isEmpty, debounceMs, slowAfterMs} = latestRef.current;

        if (!isRetry && cache && key !== null) {
            const cached = cache.get(key);

            if (cached !== undefined) {
                setState({runId, status: toResolvedStatus(cached, isEmpty), data: cached, isSlow: false, lastSuccess: cached});
                return;
            }
        }

        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        let slowTimer: ReturnType<typeof setTimeout> | undefined;
        let controller: AbortController | null = null;

        const cancel = () => {
            clearTimeout(debounceTimer);
            clearTimeout(slowTimer);
            controller?.abort();
        };

        const startRequest = () => {
            const requestController = new AbortController();
            const {signal} = requestController;
            const requestId = ++requestIdRef.current;
            const isCurrent = () => !signal.aborted && requestId === requestIdRef.current;
            const latest = latestRef.current;

            controller = requestController;
            setState((previous) => ({runId, status: "loading", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
            slowTimer = setTimeout(() => {
                setState((previous) => (previous.runId === runId && previous.status === "loading" ? {...previous, isSlow: true} : previous));
            }, slowAfterMs);

            let request: Promise<T>;
            try {
                request = latest.fetcher(latest.trimmedQuery, {signal});
            } catch (error) {
                request = Promise.reject(error);
            }

            request.then(
                (data) => {
                    if (!isCurrent()) {
                        return;
                    }
                    clearTimeout(slowTimer);
                    if (key !== null) {
                        latestRef.current.cache?.set(key, data);
                    }
                    setState({runId, status: toResolvedStatus(data, latestRef.current.isEmpty), data, isSlow: false, lastSuccess: data});
                },
                () => {
                    if (!isCurrent()) {
                        return;
                    }
                    clearTimeout(slowTimer);
                    setState((previous) => ({runId, status: "error", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
                }
            );
        };

        if (isRetry || debounceMs <= 0) {
            startRequest();
        } else {
            setState((previous) => ({runId, status: "debouncing", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
            debounceTimer = setTimeout(startRequest, debounceMs);
        }

        cancelRef.current = cancel;

        return () => {
            cancel();
            if (cancelRef.current === cancel) {
                cancelRef.current = null;
            }
        };
    }, [runId]);

    const retry = useCallback(() => {
        const currentKey = latestRef.current.key;

        if (currentKey !== null) {
            setCommand({key: currentKey, type: "retry", nonce: ++nonceRef.current});
        }
    }, []);

    const abort = useCallback(() => {
        // Cancel right away - the state update below only takes effect with the next render.
        cancelRef.current?.();
        cancelRef.current = null;

        const currentKey = latestRef.current.key;

        if (currentKey !== null) {
            setCommand({key: currentKey, type: "abort", nonce: ++nonceRef.current});
        }
    }, []);

    if (runId === null) {
        return {status: "idle", queryKey: null, data: null, staleData: null, isSlow: false, retry, abort};
    }

    if (state.runId === runId) {
        const isResolved = state.status === "success" || state.status === "empty";
        const isPending = state.status === "debouncing" || state.status === "loading";

        return {
            status: state.status,
            queryKey: isResolved ? key : null,
            data: state.data,
            staleData: isPending && !state.isSlow ? state.lastSuccess : null,
            isSlow: state.isSlow,
            retry,
            abort
        };
    }

    // The phrase (or command) changed in this render and the effect has not run yet: answer with what it is about to
    // do, so consumers never render the previous phrase's result under the new one.
    const cached = !isRetry && key !== null ? options.cache?.get(key) : undefined;

    if (cached !== undefined) {
        return {
            status: toResolvedStatus(cached, options.isEmpty ?? isNeverEmpty),
            queryKey: key,
            data: cached,
            staleData: null,
            isSlow: false,
            retry,
            abort
        };
    }

    return {
        status: isRetry || (options.debounceMs ?? DEFAULT_DEBOUNCE_MS) <= 0 ? "loading" : "debouncing",
        queryKey: null,
        data: null,
        staleData: state.lastSuccess,
        isSlow: false,
        retry,
        abort
    };
};
import {useCallback, useEffect, useRef, useState} from "react";

import type {ISuggestionsQueryResult} from "../types/ISuggestionsQueryResult";
import type {IUseSuggestionsQueryOptions} from "../types/IUseSuggestionsQueryOptions";
import type {SearchComboboxStatus} from "../types/SearchComboboxStatus";
import {useLatestRef} from "./use_latest_ref";

const DEFAULT_DEBOUNCE_MS = 200;
const DEFAULT_MIN_LENGTH = 2;
const DEFAULT_SLOW_AFTER_MS = 300;

const defaultGetCacheKey = (query: string): string => query.trim().toLowerCase().replace(/\s+/g, " ");
const isNeverEmpty = (): boolean => false;

/** `retry` and `abort` apply to the phrase they were called for; typing something else drops them. */
interface IQueryCommand {
    key: string;
    type: "retry" | "abort";
    nonce: number;
}

interface IQueryState<T> {
    /** Run (phrase + command) this state describes; a mismatch with the current run means the effect has not caught up yet. */
    runId: string | null;
    status: SearchComboboxStatus;
    data: T | null;
    isSlow: boolean;
    /** Last successful result, the source of `staleData`. Dropped whenever the hook goes idle. */
    lastSuccess: T | null;
}

const IDLE_STATE: IQueryState<never> = {runId: null, status: "idle", data: null, isSlow: false, lastSuccess: null};

const toResolvedStatus = <T>(data: T, isEmpty: (data: T) => boolean): SearchComboboxStatus => (isEmpty(data) ? "empty" : "success");

/**
 * Lifecycle of a suggestions request for a typed phrase: debounce, one `AbortController` per request, cache, retry
 * and the "slow" flag. Aborts are detected with `signal.aborted` (request libraries such as `@pg-mono/request` wrap
 * the `AbortError`) and never surface as `error`; a request id guards against late responses on top of that.
 * Until `slowAfterMs` passes the previous result stays available as `staleData`, then `isSlow` takes over.
 */
export const useSuggestionsQuery = <T>(options: IUseSuggestionsQueryOptions<T>): ISuggestionsQueryResult<T> => {
    const {query, minLength = DEFAULT_MIN_LENGTH, isEnabled = true, getCacheKey = defaultGetCacheKey} = options;
    const trimmedQuery = query.trim();
    const key = isEnabled && trimmedQuery.length >= minLength ? getCacheKey(trimmedQuery) : null;

    const [command, setCommand] = useState<IQueryCommand | null>(null);
    const [state, setState] = useState<IQueryState<T>>(IDLE_STATE);
    const requestIdRef = useRef(0);
    const nonceRef = useRef(0);
    const cancelRef = useRef<(() => void) | null>(null);
    const latestRef = useLatestRef({
        key,
        trimmedQuery,
        fetcher: options.fetcher,
        cache: options.cache,
        isEmpty: options.isEmpty ?? isNeverEmpty,
        debounceMs: options.debounceMs ?? DEFAULT_DEBOUNCE_MS,
        slowAfterMs: options.slowAfterMs ?? DEFAULT_SLOW_AFTER_MS
    });

    const activeCommand = command && command.key === key ? command : null;
    const isAborted = activeCommand?.type === "abort";
    const isRetry = activeCommand?.type === "retry";
    const runId = key === null || isAborted ? null : `${key}\u0000${activeCommand ? activeCommand.nonce : ""}`;

    useEffect(() => {
        if (command && !activeCommand) {
            setCommand(null);
        }
    }, [command, activeCommand]);

    useEffect(() => {
        if (runId === null) {
            setState((previous) => (previous.status === "idle" && previous.lastSuccess === null ? previous : IDLE_STATE));
            return;
        }

        const {cache, isEmpty, debounceMs, slowAfterMs} = latestRef.current;

        if (!isRetry && cache && key !== null) {
            const cached = cache.get(key);

            if (cached !== undefined) {
                setState({runId, status: toResolvedStatus(cached, isEmpty), data: cached, isSlow: false, lastSuccess: cached});
                return;
            }
        }

        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        let slowTimer: ReturnType<typeof setTimeout> | undefined;
        let controller: AbortController | null = null;

        const cancel = () => {
            clearTimeout(debounceTimer);
            clearTimeout(slowTimer);
            controller?.abort();
        };

        const startRequest = () => {
            const requestController = new AbortController();
            const {signal} = requestController;
            const requestId = ++requestIdRef.current;
            const isCurrent = () => !signal.aborted && requestId === requestIdRef.current;
            const latest = latestRef.current;

            controller = requestController;
            setState((previous) => ({runId, status: "loading", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
            slowTimer = setTimeout(() => {
                setState((previous) => (previous.runId === runId && previous.status === "loading" ? {...previous, isSlow: true} : previous));
            }, slowAfterMs);

            let request: Promise<T>;
            try {
                request = latest.fetcher(latest.trimmedQuery, {signal});
            } catch (error) {
                request = Promise.reject(error);
            }

            request.then(
                (data) => {
                    if (!isCurrent()) {
                        return;
                    }
                    clearTimeout(slowTimer);
                    if (key !== null) {
                        latestRef.current.cache?.set(key, data);
                    }
                    setState({runId, status: toResolvedStatus(data, latestRef.current.isEmpty), data, isSlow: false, lastSuccess: data});
                },
                () => {
                    if (!isCurrent()) {
                        return;
                    }
                    clearTimeout(slowTimer);
                    setState((previous) => ({runId, status: "error", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
                }
            );
        };

        if (isRetry || debounceMs <= 0) {
            startRequest();
        } else {
            setState((previous) => ({runId, status: "debouncing", data: null, isSlow: false, lastSuccess: previous.lastSuccess}));
            debounceTimer = setTimeout(startRequest, debounceMs);
        }

        cancelRef.current = cancel;

        return () => {
            cancel();
            if (cancelRef.current === cancel) {
                cancelRef.current = null;
            }
        };
    }, [runId]);

    const retry = useCallback(() => {
        const currentKey = latestRef.current.key;

        if (currentKey !== null) {
            setCommand({key: currentKey, type: "retry", nonce: ++nonceRef.current});
        }
    }, []);

    const abort = useCallback(() => {
        // Cancel right away - the state update below only takes effect with the next render.
        cancelRef.current?.();
        cancelRef.current = null;

        const currentKey = latestRef.current.key;

        if (currentKey !== null) {
            setCommand({key: currentKey, type: "abort", nonce: ++nonceRef.current});
        }
    }, []);

    if (runId === null) {
        return {status: "idle", queryKey: null, data: null, staleData: null, isSlow: false, retry, abort};
    }

    if (state.runId === runId) {
        const isResolved = state.status === "success" || state.status === "empty";
        const isPending = state.status === "debouncing" || state.status === "loading";

        return {
            status: state.status,
            queryKey: isResolved ? key : null,
            data: state.data,
            staleData: isPending && !state.isSlow ? state.lastSuccess : null,
            isSlow: state.isSlow,
            retry,
            abort
        };
    }

    // The phrase (or command) changed in this render and the effect has not run yet: answer with what it is about to
    // do, so consumers never render the previous phrase's result under the new one.
    const cached = !isRetry && key !== null ? options.cache?.get(key) : undefined;

    if (cached !== undefined) {
        return {
            status: toResolvedStatus(cached, options.isEmpty ?? isNeverEmpty),
            queryKey: key,
            data: cached,
            staleData: null,
            isSlow: false,
            retry,
            abort
        };
    }

    return {
        status: isRetry || (options.debounceMs ?? DEFAULT_DEBOUNCE_MS) <= 0 ? "loading" : "debouncing",
        queryKey: null,
        data: null,
        staleData: state.lastSuccess,
        isSlow: false,
        retry,
        abort
    };
};
